import { Button, Typography, Box, CircularProgress } from "@mui/material";
import type { IComment } from "../../../../../types/comment";
import { useState } from "react";
import apiService from "../../../../../service/Api";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../components/common/ShowToast";

interface StatusUpdateProps {
  comment: IComment;
  refreshComments: (comment: IComment, isDeleting: boolean) => void;
  fetchApprovedComments: () => Promise<void>;
}

export const StatusUpdate: React.FC<StatusUpdateProps> = ({
  comment,
  refreshComments,
  fetchApprovedComments,
}) => {
  const [isStatusUpdating, setIsStatusUpdating] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const determineButtonColor = (isApproved: boolean) => {
    const color = isApproved ? "secondary" : "success";
    return color;
  };

  const deleteComment = async (
    comment: IComment,
    setIsDeleting: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    setIsDeleting(true);

    try {
      await apiService.delete(`/api/chats/memorial-message/${comment.id}/`);
      refreshComments(comment, true);
      showSuccessToast("Comment deleted");
    } catch {
      showErrorToast("Failed to spam the comment,please try again");
    } finally {
      setIsDeleting(false);
    }
  };

  const updateApproveStatus = async (
    comment: IComment,
    setIsStatusUpdating: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    setIsStatusUpdating(true);

    try {
      await apiService.patch(`/api/chats/memorial-message/${comment.id}/`, {
        is_approved: !comment.isApproved,
      });

      // update the comment to new status

      let updatedComment = comment;
      updatedComment.isApproved = !comment.isApproved;
      refreshComments(updatedComment, false);
      showSuccessToast(
        `Comment ${!comment.isApproved ? "Unapproved" : "Approved"}`
      );
      fetchApprovedComments();
    } catch (error) {
      showSuccessToast(
        `Comment ${!comment.isApproved ? "Unapproved" : "Approved"}`
      );
    } finally {
      setIsStatusUpdating(false);
    }
  };

  return (
    <Box sx={{ mt: 1, mb: 1 }}>
      <Typography variant="body2">{comment.content}</Typography>

      {isStatusUpdating || isDeleting ? (
        <Box mt={1} ml={2}>
          <CircularProgress size={15} />
        </Box>
      ) : (
        <Box mt={1}>
          <Button
            color={determineButtonColor(comment.isApproved)}
            onClick={() => updateApproveStatus(comment, setIsStatusUpdating)}
            variant="text"
            size="small"
            sx={{ p: 0 }}
          >
            {comment.isApproved ? "Unapprove" : "Approve"}
          </Button>
          <Button
            onClick={() => deleteComment(comment, setIsDeleting)}
            color="error"
            variant="text"
            size="small"
            sx={{ p: 0 }}
          >
            Delete
          </Button>
        </Box>
      )}
    </Box>
  );
};
